import Vue from 'vue';
import VeeValidate from 'vee-validate';
import { Attendee } from './types/event/DtoAttendee';
import StringHelpers from '../common/services/StringHelpers';

VeeValidate.Validator.extend('maxQuantity', {
    getMessage: (field: string, arg: string[]) => {
        if (arg.length > 1 && arg[1]) {
            return arg[1];
        }
        return `Max is ${arg[0]}`;
    },
    validate(value: number, arg: string[]) {
        return value <= parseInt(arg[0]);
    }
});

VeeValidate.Validator.extend('dangerContent', {
    getMessage: (field: string, arg: boolean) => {
        return arg ? `Value cannot contain '<' or '>'.` : ``;
    },
    validate(value: string, arg: boolean) {
        var forbiddenChars = ['<', '>']
        return !arg || forbiddenChars.filter(x => value.indexOf(x) > -1).length == 0;
    }
});

VeeValidate.Validator.extend('donationPrice', {
    getMessage: (field: string, arg: number[]) => {
        const minimumPrice = arg[0];
        return arg ? `Donation amount is lower than the minimum donation ($${minimumPrice.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}) for this product.` : ``;
    },
    validate(value: string, arg: number[]) {
        if (!value || !arg) {
            return true;
        }
        const memberPrice = parseFloat(value);
        const minimumPrice = arg[0];
        const isValid = memberPrice == 0 || memberPrice >= minimumPrice;
        return isValid;
    }
});

VeeValidate.Validator.extend('customMatch', {
    getMessage: (field: string) => {
        return `The ${field} does not match.`;
    },

    validate: (value: string, [otherValue]: string[]) => {
        return value === otherValue;
    }
});

VeeValidate.Validator.extend('numericLength', {
    getMessage: (field: string, params: { [key: string]: any }) => {
        const length = params[0];
        return `The ${field} field must have exactly ${length} numeric characters.`;
    },

    validate: (value: string, [length]: number[]) => {
        const numericValue = value.replace(/\D/g, '');  // Remove non-numeric characters
        return numericValue.length === parseInt(length.toString(), 10);
    }
});

// Ensures the entered email is unique among provided attendees.
// Accepts either an array of Attendee objects or plain email strings.
// - If attendees are Attendee objects: allow one match (self).
// - If attendees are strings: disallow any match (self excluded).
VeeValidate.Validator.extend('uniqueEmail', {
    getMessage: () => 'This email is already in use for this event. A unique address is required for each attendee.',

    validate: (value: string, { attendees }: { attendees?: Attendee[] | string[] } = {}) => {
        if (!attendees || !Array.isArray(attendees)) {
            return true; // If no attendees are provided, pass validation
        }

        const current = value?.toLowerCase?.() ?? '';
        const isStringList = typeof attendees[0] === 'string';

        const otherEmails = attendees
            .map(a => (isStringList ? (a as string) : (a as Attendee).email)?.toLowerCase())
            .filter(Boolean);

        const matchCount = otherEmails.filter(e => e === current).length;

        // allow one match if Attendee objects (self included), none if strings
        return isStringList ? matchCount === 0 : matchCount <= 1;
    }
});

VeeValidate.Validator.extend('noHtml', {
    getMessage: (field: string) => {
        return `The ${field} field cannot contain HTML.`;
    },

    validate: (value: string) => {
        return !/<[a-z][\s\S]*>/i.test(value);
    }
});

VeeValidate.Validator.extend('multi_email', {
    getMessage: () => 'The Email field must contain valid email(s).',
    validate: (value: string) => {
        if (!value) return true; // let "required" handle empties
        return StringHelpers.isValidEmail(value);
    }
});

VeeValidate.Validator.extend('requiredFile', {
    getMessage: (field: string) => `${field} is required.`,
    validate: (value: unknown): boolean => {
        // Handle undefined / null
        if (value == null) {
            return false;
        }

        // We expect a string for the hidden field
        if (typeof value !== 'string') {
            return false;
        }

        // Trim whitespace
        const trimmed = value.trim();

        // Must be non-empty and must not equal an empty serialized array
        if (trimmed === '' || trimmed === '[]') {
            return false;
        }

        return true;
    }
});

Vue.use(VeeValidate, {
    events: 'input|blur',
    useConstraintAttrs: false
});